import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { useTranslations } from 'next-intl';

import { getThemeColor } from '@/common/ActionStatusSummary';
import { ActionStatusSummaryIdentifier } from '@/common/__generated__/graphql';
import dayjs from '@/common/dayjs';
import {
  getActionTaskTermContext,
  getActionTermContext,
  getIndicatorTermContext,
} from '@/common/i18n';
import { PhaseTimeline } from '@/components/actions/PhaseTimeline';
import ActionAttribute from '@/components/common/ActionAttribute';
import Icon from '@/components/common/Icon';
import PlanChip from '@/components/plans/PlanChip';

import { getTaskCounts } from './cells/TasksStatusCell';
import { type ActionListAction, type ActionListPlan, type ColumnConfig } from './dashboard.types';
import { getActionPlanImage } from './dashboard.utils';

const TooltipTitle = styled.p`
  font-weight: ${(props) => props.theme.fontWeightBold};
  margin-bottom: ${(props) => props.theme.spaces.s025};
`;

const ResponsibleTooltipList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ResponsibleTooltipListItem = styled.li``;

const TaskTooltip = styled.div``;

const StatusLabel = styled.div<{ $color: string | undefined }>`
  &:before {
    content: '';
    display: inline-block;
    width: 1em;
    height: 1em;
    margin-right: 0.5em;
    margin-bottom: -0.1em;
    border-radius: 50%;
    background-color: ${(props) => props.$color};
    color: ${(props) => props.$color};
  }
`;

interface TooltipProps {
  action: ActionListAction;
}

interface TooltipWithPlanProps extends TooltipProps {
  plan: ActionListPlan;
}

interface AttributeTooltipProps {
  attribute: ActionListAction['attributes'][number];
  attributeType: NonNullable<ColumnConfig['attributeType']>;
}

export const OrganizationTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  const t = useTranslations();
  const organizationTerm = plan?.generalContent?.organizationTerm;

  return (
    <div>
      <TooltipTitle>{t('primary-organization', { context: organizationTerm })}</TooltipTitle>
      {action.primaryOrg?.name}
    </div>
  );
};

export const TasksTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  const t = useTranslations();
  const taskCounts = getTaskCounts(action.tasks, plan, t);

  if (taskCounts.total < 1)
    return (
      <div>
        <TooltipTitle>{t('action-no-tasks', getActionTaskTermContext(plan))}</TooltipTitle>
      </div>
    );

  return (
    <TaskTooltip>
      <TooltipTitle>{t('action-tasks', getActionTaskTermContext(plan))} </TooltipTitle>
      <table>
        <tbody>
          {taskCounts.completed > 0 && (
            <tr>
              <td>{t('tasks-completed', getActionTaskTermContext(plan))}</td>
              <td>{taskCounts.completed}</td>
            </tr>
          )}
          {taskCounts.late > 0 && (
            <tr>
              <td>{t('tasks-late', getActionTaskTermContext(plan))}</td>
              <td>{taskCounts.late}</td>
            </tr>
          )}
          {taskCounts.onTime > 0 && (
            <tr>
              <td>{t('tasks-on-time', getActionTaskTermContext(plan))}</td>
              <td>{taskCounts.onTime}</td>
            </tr>
          )}
        </tbody>
      </table>
    </TaskTooltip>
  );
};

const StyledPhaseTimelineContainer = styled.div`
  margin: ${({ theme }) => theme.spaces.s100} 0;
`;

export const ImplementationPhaseTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  const t = useTranslations();
  const theme = useTheme();

  const activePhase = action.implementationPhase;
  const merged = action.mergedWith;
  const status = action.statusSummary;

  if (!plan) {
    return null;
  }

  const getMergedName = (
    mergedWith: NonNullable<ActionListAction['mergedWith']>,
    planId: string
  ) => {
    if (mergedWith.plan.id !== planId) {
      return `${mergedWith.plan.shortName} ${mergedWith.identifier}`;
    } else {
      return mergedWith.identifier;
    }
  };

  const statusDisplay = (
    <StatusLabel $color={getThemeColor(action.color ?? 'grey050', theme)}>
      {status.label}
    </StatusLabel>
  );

  // If action is merged, display merged status
  if (merged) {
    return (
      <TooltipTitle>
        {` ${t(
          'action-status-merged',
          getActionTermContext(plan)
        )}: ${getMergedName(merged, plan.id)}.`}
      </TooltipTitle>
    );
  }

  // If action has no active phase or it's cancelled, or plan has no implementation phases : display only status
  if (!activePhase || status?.identifier === ActionStatusSummaryIdentifier.Cancelled) {
    return statusDisplay;
  }

  return (
    <div>
      <TooltipTitle>{t('action-implementation-phase')}:</TooltipTitle>
      <StyledPhaseTimelineContainer>
        {!!action.implementationPhase && (
          <PhaseTimeline
            layout="vertical"
            activePhase={action.implementationPhase}
            phases={plan.actionImplementationPhases}
            isContinuous={action.scheduleContinuous}
          />
        )}
      </StyledPhaseTimelineContainer>
    </div>
  );
};

export const ResponsiblePartiesTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  const t = useTranslations();
  const theme = useTheme();
  const organizationTerm = plan?.generalContent?.organizationTerm;

  const parties = action.responsibleParties ?? [];

  const getResponsiblePartyLabel = (party: {
    organization: { abbreviation: string | null; name: string } | null;
  }) => party.organization?.abbreviation || party.organization?.name;

  if (parties.length < 1)
    return (
      <div>
        <TooltipTitle>{t('responsible-parties', { context: organizationTerm })}</TooltipTitle>
      </div>
    );

  return (
    <div>
      <TooltipTitle>{t('responsible-parties', { context: organizationTerm })}</TooltipTitle>
      {/* TODO: Fix missing type property. hasContactPerson is added to actions higher in the component tree */}
      {parties.find((party) => party.hasContactPerson) && (
        <strong>{t('with-contact-persons')}:</strong>
      )}
      <ResponsibleTooltipList>
        {parties.map((party) => {
          const label = getResponsiblePartyLabel(party);

          return party.hasContactPerson && label ? (
            <ResponsibleTooltipListItem key={party.id}>
              <Icon
                name={party.hasContactPerson ? 'dot-circle' : 'circle-outline'}
                color={theme.actionOnTimeColor}
                width="1em"
                height="1em"
              />{' '}
              {label}
            </ResponsibleTooltipListItem>
          ) : null;
        })}
      </ResponsibleTooltipList>
      {parties.find((party) => !party.hasContactPerson) && (
        <strong>{t('without-contact-person')}:</strong>
      )}
      <ResponsibleTooltipList>
        {parties.map((party) => {
          const label = getResponsiblePartyLabel(party);

          return !party.hasContactPerson && label ? (
            <ResponsibleTooltipListItem key={party.id}>
              <Icon
                name={party.hasContactPerson ? 'dot-circle' : 'circle-outline'}
                color={theme.actionOnTimeColor}
                width="1em"
                height="1em"
              />{' '}
              {label}
            </ResponsibleTooltipListItem>
          ) : null;
        })}
      </ResponsibleTooltipList>
    </div>
  );
};

export const IndicatorsTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  const t = useTranslations();
  const theme = useTheme();
  const indicatorTermContext = getIndicatorTermContext(plan);

  const hasIndicators = action.indicatorsCount != null && action.indicatorsCount > 0;
  const hasGoals = action.hasIndicatorsWithGoals;
  return (
    <div>
      <TooltipTitle>{t('indicators', indicatorTermContext)}</TooltipTitle>
      <Icon.Tachometer
        color={hasIndicators ? theme.graphColors.green070 : theme.graphColors.grey030}
        height="1.2em"
        width="1.2em"
      />
      {hasIndicators
        ? ` ${t('indicators', indicatorTermContext)}: ${action.indicatorsCount}`
        : ` ${t('no-defined-indicators')}`}
      <br />
      <Icon.Bullseye
        color={hasGoals ? theme.graphColors.green070 : theme.graphColors.grey030}
        height="1.2em"
        width="1.2em"
      />
      {hasGoals ? ` ${t('has-goals')}` : ` ${t('indicator-time-no-goals')}`}
    </div>
  );
};

export const LastUpdatedTooltipContent = ({ action }: TooltipProps) => {
  const t = useTranslations();

  return (
    <div>
      <TooltipTitle>{t('latest-update')}</TooltipTitle>
      {dayjs(action.updatedAt).format('L')}
    </div>
  );
};

export const PlanTooltipContent = ({ action, plan }: TooltipWithPlanProps) => {
  return (
    <div>
      <PlanChip
        planShortName={action.plan?.shortName || action.plan?.name}
        planImage={getActionPlanImage(action.plan, plan)}
        size="md"
      />
    </div>
  );
};

export const AttributeTooltipContent = ({ attribute, attributeType }: AttributeTooltipProps) => {
  return (
    <ActionAttribute
      {...({ attribute, attributeType, notitle: true } as Parameters<typeof ActionAttribute>[0])}
    />
  );
};
